import { createContext, type ReactNode, useContext } from 'react'

/**
 * Identifies which AD operational surface the user is currently working in.
 *
 * This is an operational context, not a Boum right. Area entry is still gated
 * by rights, but how a shared component behaves (which cards, actions, and
 * panels it exposes) is derived from the active remote operations area.
 */
export type AdRemoteOperationsArea =
  | 'ad-fleet-map'
  | 'remote-assistance-operator'
  | 'rider-support'

interface AdRemoteOperationsAreaContextType {
  remoteOperationsArea: AdRemoteOperationsArea
}

const AdRemoteOperationsAreaContext = createContext<
  AdRemoteOperationsAreaContextType | undefined
>(undefined)

export const AdRemoteOperationsAreaProvider = ({
  remoteOperationsArea,
  children,
}: {
  remoteOperationsArea: AdRemoteOperationsArea
  children: ReactNode
}) => (
  <AdRemoteOperationsAreaContext.Provider value={{ remoteOperationsArea }}>
    {children}
  </AdRemoteOperationsAreaContext.Provider>
)

export const useAdRemoteOperationsArea = () => {
  const adRemoteOperationsAreaContextValue = useContext(
    AdRemoteOperationsAreaContext,
  )
  if (!adRemoteOperationsAreaContextValue) {
    throw new Error(
      'useAdRemoteOperationsArea must be used within an AdRemoteOperationsAreaProvider',
    )
  }
  return adRemoteOperationsAreaContextValue
}

/**
 * Remote operations area for surfaces that may render outside an
 * AdRemoteOperationsAreaProvider, e.g. Safety Driver Guidance, which reuses AD
 * providers without being an AD remote operations area. Returns undefined
 * instead of throwing.
 */
export const useOptionalAdRemoteOperationsArea = () =>
  useContext(AdRemoteOperationsAreaContext)?.remoteOperationsArea
